import React, { useEffect, useMemo, useState } from 'react';
import { getAgentesHorarios, AgenteHorario } from '../services/horarios.service';
import {
  asignarJustificacionMasiva,
  getTiposJustificacion,
  JustificacionTipo,
} from '../services/justificaciones.service';
import { getErrorMessage } from '../utils/errorHandler';

const getTodayIso = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const AsignacionJustificacionesPage: React.FC = () => {
  const [agentes, setAgentes] = useState<AgenteHorario[]>([]);
  const [tipos, setTipos] = useState<JustificacionTipo[]>([]);
  const [selectedAgentes, setSelectedAgentes] = useState<string[]>([]);
  const [tipoJustificacionId, setTipoJustificacionId] = useState('');
  const [fecha, setFecha] = useState(getTodayIso());
  const [fechaHasta, setFechaHasta] = useState(getTodayIso());
  const [detalle, setDetalle] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    void Promise.all([getAgentesHorarios(), getTiposJustificacion()])
      .then(([agentesResp, tiposResp]) => {
        const agentesData = Array.isArray(agentesResp.data) ? agentesResp.data : [];
        const tiposData = Array.isArray(tiposResp.data) ? tiposResp.data.filter((item) => item.activo) : [];
        setAgentes(agentesData);
        setTipos(tiposData);
        if (tiposData[0]?._id) {
          setTipoJustificacionId(tiposData[0]._id);
        }
      })
      .catch((err) => setError(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  const filteredAgentes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agentes;
    return agentes.filter((agente) => {
      const target = `${agente.nombre} ${agente.documento || ''} ${agente.idAgente || ''}`.toLowerCase();
      return target.includes(q);
    });
  }, [agentes, search]);

  const toggleAgente = (id: string) => {
    setSelectedAgentes((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    if (filteredAgentes.length === 0) return;
    const filteredIds = filteredAgentes.map((a) => a._id);
    const allSelected = filteredIds.every((id) => selectedAgentes.includes(id));
    if (allSelected) {
      setSelectedAgentes((prev) => prev.filter((id) => !filteredIds.includes(id)));
      return;
    }
    setSelectedAgentes((prev) => Array.from(new Set([...prev, ...filteredIds])));
  };

  const onAsignar = async () => {
    setError('');
    setSuccess('');

    if (!tipoJustificacionId) {
      setError('Seleccione un tipo de justificacion');
      return;
    }

    if (fechaHasta && fechaHasta < fecha) {
      setError('La fecha "Hasta" no puede ser menor que la fecha "Desde".');
      return;
    }

    if (selectedAgentes.length === 0) {
      setError('Seleccione al menos un agente');
      return;
    }
    //TODO : LEGAJOS de Personal , Adelantos , Certificados , Licencias , Permisos , Vacaciones,Solicitud de Cambio de Turno , 
    // Aviso de Llegada Tarde, apercibimiento modelos de notas para imprimir,

    setSaving(true);
    try {
      const resp = await asignarJustificacionMasiva({
        tipoJustificacionId,
        agenteIds: selectedAgentes,
        fecha,
        fechaHasta: fechaHasta || undefined,
        detalle: detalle || undefined,
        estado: 'APROBADA',
      });
      const creadas = resp.data?.creadas ?? 0;
      const omitidas = resp.data?.omitidas ?? 0;
      setSuccess(`Asignacion completada. Creadas: ${creadas}. Omitidas: ${omitidas}.`);
      setSelectedAgentes([]);
      setDetalle('');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = selectedAgentes.length;

  return (
    <>
      <h1 className="page-title">Novedades - Asignacion Masiva</h1>

      <div className="card">
        <h2 className="card-title">Asignar justificacion a uno o mas colaboradores</h2>
        {error && <div className="error-msg">{error}</div>}
        {success && <div className="success-msg">{success}</div>}



        <div className="form-grid">
        
        <div className="form-group">
            <label>Buscar Legajo</label>
            <input
              placeholder="Nombre, DNI o ID legajo"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>


          <div className="form-group">
            <label>Desde</label>
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>

          <div className="form-group">
            <label>Hasta</label>
            <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
          </div>

          <div className="form-group">
            <label>Tipo de Novedad</label>
            <select value={tipoJustificacionId} onChange={(e) => setTipoJustificacionId(e.target.value)}>
              <option value="">Seleccionar...</option>
              {tipos.map((tipo) => (
                <option key={tipo._id} value={tipo._id}>
                  {tipo.nombre} ({tipo.duracionHoras}h)
                </option>
              ))}
            </select>
          </div>
         
        </div>

        <div className="form-group" style={{ marginBottom: '12px' }}>
          <label>Detalle (opcional)</label>
          <textarea
            rows={2}
            value={detalle}
            onChange={(e) => setDetalle(e.target.value)}
            placeholder="Ejemplo: Licencia por enfermedad"
          />
        </div>

        <div className="table-wrapper" style={{ marginBottom: '12px' }}>
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={toggleAll}
                    checked={filteredAgentes.length > 0 && filteredAgentes.every((a) => selectedAgentes.includes(a._id))}
                  />
                </th>
                <th>Nombre</th>
                <th>DNI</th>
                <th>ID Legajo</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgentes.map((agente) => (
                <tr key={agente._id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedAgentes.includes(agente._id)}
                      onChange={() => toggleAgente(agente._id)}
                    />
                  </td>
                  <td>{agente.nombre}</td>
                  <td>{agente.documento || '-'}</td>
                  <td>{agente.idAgente || '-'}</td>
                </tr>
              ))}
              {filteredAgentes.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center' }}>No hay colaboradores para mostrar</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="filter-actions">
          <button className="btn btn-primary" type="button" disabled={saving || loading} onClick={onAsignar}>
            {saving ? 'Asignando...' : 'Asignar'}
          </button>
          <span style={{ alignSelf: 'center', color: '#555', fontSize: '0.88rem' }}>
            Seleccionados: {selectedCount}
          </span>
        </div>
      </div>
    </>
  );
};

export default AsignacionJustificacionesPage;
